/**
 * Verify Page - Handle magic link verification for platform admins
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ApiError } from '../lib/api-client';

type VerifyState = 'loading' | 'success' | 'error';

export function VerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const [state, setState] = useState<VerifyState>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setState('error');
      setError('Invalid or missing token');
      return;
    }

    const verifyToken = async () => {
      try {
        await login(token);
        setState('success');
        setTimeout(() => navigate('/tenants', { replace: true }), 1500);
      } catch (err) {
        setState('error');
        if (err instanceof ApiError) {
          if (err.code === 'auth_magic_link_expired') {
            setError('This link has expired. Please request a new one.');
          } else if (err.code === 'auth_magic_link_invalid') {
            setError('This link is invalid. Please request a new one.');
          } else if (err.code === 'platform_admin_not_found') {
            setError('This email is not authorized for the control plane.');
          } else {
            setError(err.message);
          }
        } else {
          setError('Verification failed. Please try again.');
        }
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated && state !== 'loading') {
      navigate('/tenants', { replace: true });
    }
  }, [isAuthenticated, state, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-xl mb-4">
              <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Control Plane</h1>
          </div>

          {state === 'loading' && (
            <div className="text-center py-4">
              <Loader2 className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Verifying...</h2>
              <p className="text-gray-600 dark:text-gray-400">Please wait while we sign you in.</p>
            </div>
          )}

          {state === 'success' && (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Success!</h2>
              <p className="text-gray-600 dark:text-gray-400">Redirecting...</p>
            </div>
          )}

          {state === 'error' && (
            <div className="text-center py-4">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Verification Failed</h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login', { replace: true })}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl transition-all shadow-lg hover:shadow-xl"
              >
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
